export type GroupCountRow = { value: string; count: number };

export type DailyCountRow = { day: string; count: number };

export type FemaDepthCrossRow = {
  fema: string;
  depth: string;
  count: number;
};

export type StructureExposureByDayRow = {
  day: string;
  state: string;
  total: number;
  categories: Record<string, number>;
};

/** Per-county priority_score segment counts for Top counties UI. */
export type CountyPriorityBreakdownRow = {
  county: string;
  total: number;
  byPriority: Array<{ priority: string; count: number }>;
};

export type LayerQueryLayerResult = {
  id: string;
  name: string;
  kind: 'floodingBins' | 'tingOutages' | string;
  timeField?: string;
  totalCount: number | null;
  groupCounts: Record<string, GroupCountRow[]>;
  /** FloodingBins: top counties keyed by STATE_ABBR. */
  countiesByState?: Record<string, GroupCountRow[]>;
  /**
   * FloodingBins: top counties with priority_score breakdown, keyed by
   * STATE_ABBR.
   */
  countyPriorityBreakdown?: Record<string, CountyPriorityBreakdownRow[]>;
  /** FloodingBins: FEMA × depth joint distribution. */
  femaDepthCross?: FemaDepthCrossRow[];
  /** FloodingBins: occupancy sums by UTC day × state. */
  structureExposureByDay?: StructureExposureByDayRow[];
  stats: Record<string, number | null>;
  dailyCounts: DailyCountRow[];
  exemplars: Array<Record<string, unknown>>;
  queriesReady?: string[];
  notes?: string[];
  queryErrors?: Array<{ query: string; error: string }>;
};

export type LayerQueryResults = {
  window: {
    startIso: string | null;
    endIso: string | null;
    label: string | null;
  };
  layers: LayerQueryLayerResult[];
};

export type LayerStatsAutomatedPayload = {
  LayerQueryResults: LayerQueryResults | null;
  notes?: string[];
  error?: string;
};

export function parseLayerStatsPayload(
  dataString: unknown
): LayerStatsAutomatedPayload | null {
  if (!dataString) return null;
  try {
    const parsed =
      typeof dataString === 'string' ? JSON.parse(dataString) : dataString;
    if (!parsed || typeof parsed !== 'object') return null;
    const obj = parsed as Record<string, unknown>;
    if (!('LayerQueryResults' in obj) && !obj.error && !obj.notes) {
      return null;
    }
    return obj as LayerStatsAutomatedPayload;
  } catch {
    return null;
  }
}

export function formatPeople(value: unknown): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return value.toLocaleString(undefined, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  });
}

export function formatCount(value: unknown): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return Math.round(value).toLocaleString();
}

/**
 * Pretty date for Ting outage displaydate (epoch ms, ISO, or ArcGIS
 * /Date(ms)/).
 */
export function formatOutageDisplayDate(value: unknown): string {
  if (value == null || value === '') return '—';
  const opts: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  };
  if (typeof value === 'string') {
    const trimmed = value.trim();
    // Calendar date only — avoid UTC midnight shifting the local day.
    const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
    if (ymd) {
      const d = new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
      return d.toLocaleDateString('en-US', opts);
    }
    const arcgis = /^\/Date\((-?\d+)\)\/$/.exec(trimmed);
    if (arcgis) {
      const d = new Date(Number(arcgis[1]));
      if (Number.isNaN(d.getTime())) return '—';
      return d.toLocaleDateString('en-US', opts);
    }
    const parsed = Date.parse(trimmed);
    if (Number.isNaN(parsed)) return '—';
    return new Date(parsed).toLocaleDateString('en-US', opts);
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', opts);
  }
  return '—';
}

/** ArcGIS recoveredPercentage is 0–1; display as percent with one decimal. */
export function formatRecoveryPercent(value: unknown): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  const pct = value <= 1 ? value * 100 : value;
  return `${pct.toFixed(1)}%`;
}

export function peakDay(
  dailyCounts: DailyCountRow[] | undefined | null
): { day: string; count: number } | null {
  if (!dailyCounts?.length) return null;
  let best: DailyCountRow | null = null;
  for (const row of dailyCounts) {
    if (
      !row?.day ||
      typeof row.count !== 'number' ||
      !Number.isFinite(row.count)
    ) {
      continue;
    }
    if (!best || row.count > best.count) best = row;
  }
  return best;
}

/** Destroyed + Major from FEMA groupCounts. */
export function severeFemaCount(
  groupCounts: Record<string, GroupCountRow[]> | undefined | null
): number {
  const rows = groupCounts?.fema_classification ?? [];
  let total = 0;
  for (const row of rows) {
    const v = (row.value || '').toLowerCase();
    if (v === 'destroyed' || v === 'major') {
      total += typeof row.count === 'number' ? row.count : 0;
    }
  }
  return total;
}

export function groupTotal(rows: GroupCountRow[] | undefined | null): number {
  if (!rows?.length) return 0;
  return rows.reduce(
    (sum, r) => sum + (typeof r.count === 'number' ? r.count : 0),
    0
  );
}

export function topGroupRows(
  rows: GroupCountRow[] | undefined | null,
  limit: number
): GroupCountRow[] {
  if (!rows?.length) return [];
  return [...rows]
    .sort((a, b) => (b.count || 0) - (a.count || 0))
    .slice(0, limit);
}

export function windowLabel(
  results: LayerQueryResults | null | undefined
): string | null {
  if (!results?.window) return null;
  if (results.window.label) return results.window.label;
  if (results.window.startIso && results.window.endIso) {
    return `${results.window.startIso} → ${results.window.endIso}`;
  }
  return null;
}

export function findLayerByKind(
  layers: LayerQueryLayerResult[],
  kind: string
): LayerQueryLayerResult | undefined {
  return layers.find((l) => l.kind === kind);
}

/**
 * Packs that have a dedicated Analytics card (everything else uses the generic
 * card).
 */
export const ANALYTICS_WIDGET_KINDS = [
  'floodingBins',
  'tingOutages',
  'precipTotal',
  'nwsWatches',
  'hurricaneCone',
  'hurricaneForecast',
  'hurricaneObserved',
  'streamGauges',
  'wildfirePerimeters',
  'wildfireIncidents',
  'generic',
] as const;

export type AnalyticsWidgetKind = (typeof ANALYTICS_WIDGET_KINDS)[number];

export function analyticsWidgetKind(
  kind: string | undefined | null
): AnalyticsWidgetKind {
  if (kind && (ANALYTICS_WIDGET_KINDS as readonly string[]).includes(kind)) {
    return kind as AnalyticsWidgetKind;
  }
  return 'generic';
}

/** One card per selected pack. Does not invent empty Flood/Power placeholders. */
export function analyticsCardsToRender(
  layers: LayerQueryLayerResult[]
): Array<{ kind: AnalyticsWidgetKind; layer: LayerQueryLayerResult }> {
  return (layers || []).map((layer) => ({
    kind: analyticsWidgetKind(layer.kind),
    layer,
  }));
}

export function formatLayerStatsDate(value: unknown): string {
  if (value == null || value === '') return '—';
  if (typeof value === 'number' && Number.isFinite(value)) {
    const d = new Date(value);
    return Number.isNaN(d.getTime())
      ? String(value)
      : d.toISOString().slice(0, 16).replace('T', ' ');
  }
  const parsed = Date.parse(String(value));
  if (!Number.isNaN(parsed)) {
    return new Date(parsed).toISOString().slice(0, 16).replace('T', ' ');
  }
  return String(value);
}

export function formatAcres(value: unknown): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return `${Math.round(value).toLocaleString()} ac`;
}

export function formatNumberish(value: unknown, digits = 1): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return value.toLocaleString(undefined, { maximumFractionDigits: digits });
}

/** Union of day keys from multiple daily series, sorted ascending. */
export function unionDays(
  ...series: Array<DailyCountRow[] | undefined | null>
): string[] {
  const set = new Set<string>();
  for (const rows of series) {
    for (const row of rows || []) {
      if (row?.day) set.add(row.day);
    }
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}

export function countsAlignedToDays(
  dailyCounts: DailyCountRow[] | undefined | null,
  days: string[]
): number[] {
  const map = new Map((dailyCounts || []).map((r) => [r.day, r.count]));
  return days.map((d) => map.get(d) ?? 0);
}

export function sparklinePoints(values: number[]): string {
  if (!values.length) return '';
  const max = Math.max(...values, 0.001);
  return values
    .map((value, index) => {
      const x = (index / Math.max(values.length - 1, 1)) * 100;
      const y = 36 - (value / max) * 28;
      return `${x},${y}`;
    })
    .join(' ');
}

/** ArcGIS occupancy field names (not *_sum). */
export const FLOOD_OCCUPANCY_FIELDS = [
  'Residential',
  'Agriculture',
  'Assembly',
  'Commercial',
  'Education',
  'Government',
  'Industrial',
  'Unclassified',
  'Utility_and_Misc',
] as const;

/** Window-level occupancy stats keys on layer.stats. */
export const FLOOD_OCCUPANCY_KEYS = [
  'Residential_sum',
  'Agriculture_sum',
  'Assembly_sum',
  'Commercial_sum',
  'Education_sum',
  'Government_sum',
  'Industrial_sum',
  'Unclassified_sum',
  'Utility_and_Misc_sum',
] as const;

export function occupancyLabel(key: string): string {
  const base = key.replace(/_sum$/, '');
  if (base === 'Utility_and_Misc') return 'Utility & Miscellaneous';
  return base.replace(/_/g, ' ');
}

/** Sentinel for Analytics Flood impact "All states" filter. */
export const ALL_STATES_FILTER = '__ALL__';

/**
 * Shared Flood impact / FloodingBins Demo widget filters (persisted on Layers
 * DS).
 */
export type FloodWidgetFilters = {
  /** Null / ALL_STATES_FILTER = no STATE_ABBR clause. */
  stateAbbr?: string | null;
  /** Null = no confidence_overall clause; otherwise >= threshold. */
  confidenceOverallMin?: number | null;
  /**
   * Demo-only Filters checkboxes (ignored by COMS AGOL WHERE). Defaults:
   * Resource Capacity off; Water / Population / Power on.
   */
  demoResourceCapacity?: boolean;
  demoWaterLevels?: boolean;
  demoPopulationImpacted?: boolean;
  demoPowerOutages?: boolean;
};

/** Confidence_overall >= options shown in the Flood filter bar. */
export const CONFIDENCE_OVERALL_MIN_OPTIONS = [0.5, 0.7, 0.9] as const;

function normalizeDemoFlag(
  value: boolean | undefined,
  defaultValue: boolean
): boolean {
  return typeof value === 'boolean' ? value : defaultValue;
}

export function normalizeFloodWidgetFilters(
  raw?: FloodWidgetFilters | null
): FloodWidgetFilters {
  const stateAbbr =
    typeof raw?.stateAbbr === 'string' && raw.stateAbbr.trim()
      ? raw.stateAbbr.trim()
      : ALL_STATES_FILTER;
  const min = raw?.confidenceOverallMin;
  const confidenceOverallMin =
    typeof min === 'number' && Number.isFinite(min) ? min : null;
  return {
    stateAbbr,
    confidenceOverallMin,
    demoResourceCapacity: normalizeDemoFlag(raw?.demoResourceCapacity, false),
    demoWaterLevels: normalizeDemoFlag(raw?.demoWaterLevels, true),
    demoPopulationImpacted: normalizeDemoFlag(
      raw?.demoPopulationImpacted,
      true
    ),
    demoPowerOutages: normalizeDemoFlag(raw?.demoPowerOutages, true),
  };
}

/** Prefer TN when present; otherwise first state (sorted). */
export function defaultCountyState(
  countiesByState: Record<string, GroupCountRow[]> | undefined | null,
  fallbackStates?: string[]
): string {
  const keys = Object.keys(countiesByState || {}).filter(Boolean);
  const states = keys.length ? keys : (fallbackStates || []).filter(Boolean);
  if (states.includes('TN')) return 'TN';
  return [...states].sort((a, b) => a.localeCompare(b))[0] || '';
}

export function countiesForState(
  countiesByState: Record<string, GroupCountRow[]> | undefined | null,
  state: string,
  limit = 5
): GroupCountRow[] {
  if (!state || state === ALL_STATES_FILTER || !countiesByState?.[state]) {
    return [];
  }
  return topGroupRows(countiesByState[state], limit);
}

/**
 * Top counties for one state, or across all states when `state` is
 * ALL_STATES_FILTER. Prefers global COUNTY_NAME rows when aggregating;
 * otherwise flattens as "County (ST)".
 */
export function countiesForStateOrAll(
  countiesByState: Record<string, GroupCountRow[]> | undefined | null,
  state: string,
  limit = 5,
  globalCountyRows?: GroupCountRow[] | null
): GroupCountRow[] {
  if (state === ALL_STATES_FILTER) {
    if (globalCountyRows?.length) {
      return topGroupRows(globalCountyRows, limit);
    }
    const flattened: GroupCountRow[] = [];
    for (const [st, rows] of Object.entries(countiesByState || {})) {
      if (!st) continue;
      for (const row of rows || []) {
        if (!row?.value) continue;
        flattened.push({
          value: `${row.value} (${st})`,
          count: row.count,
        });
      }
    }
    return topGroupRows(flattened, limit);
  }
  return countiesForState(countiesByState, state, limit);
}

function numericAttr(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return Number.NEGATIVE_INFINITY;
}

/** Sort Priority hotspots: priority_score DESC, then population DESC. */
export function sortFloodHotspotExemplars(
  exemplars: Array<Record<string, unknown>> | undefined | null
): Array<Record<string, unknown>> {
  return [...(exemplars || [])].sort((a, b) => {
    const priorityDiff =
      numericAttr(b.priority_score) - numericAttr(a.priority_score);
    if (priorityDiff !== 0) return priorityDiff;
    return numericAttr(b.population) - numericAttr(a.population);
  });
}

/**
 * Sort then keep one row per BIN_ID (FloodingBins has multiple OBJECTIDs per H3
 * bin).
 */
export function prepareFloodHotspotExemplars(
  exemplars: Array<Record<string, unknown>> | undefined | null,
  limit = 8
): Array<Record<string, unknown>> {
  const sorted = sortFloodHotspotExemplars(exemplars);
  const seen = new Set<string>();
  const out: Array<Record<string, unknown>> = [];
  for (const row of sorted) {
    const bin = row.BIN_ID;
    const key =
      bin != null && String(bin).trim() !== ''
        ? `bin:${String(bin)}`
        : row.OBJECTID != null
          ? `oid:${String(row.OBJECTID)}`
          : null;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(row);
    if (out.length >= limit) break;
  }
  return out;
}

/** Display row for Top counties with optional priority segments. */
export type CountyPriorityDisplayRow = {
  label: string;
  total: number;
  byPriority: Array<{ priority: string; count: number }>;
};

/**
 * Top counties with priority_score breakdown for a state (or all states). Falls
 * back to plain county totals when breakdown is absent.
 */
export function countyPriorityBreakdownForStateOrAll(
  breakdown: Record<string, CountyPriorityBreakdownRow[]> | undefined | null,
  state: string,
  limit = 5,
  fallback?: {
    countiesByState?: Record<string, GroupCountRow[]> | null;
    globalCountyRows?: GroupCountRow[] | null;
  }
): CountyPriorityDisplayRow[] {
  if (breakdown && Object.keys(breakdown).length > 0) {
    if (state === ALL_STATES_FILTER) {
      const flattened: CountyPriorityDisplayRow[] = [];
      for (const [st, rows] of Object.entries(breakdown)) {
        if (!st) continue;
        for (const row of rows || []) {
          if (!row?.county) continue;
          flattened.push({
            label: `${row.county} (${st})`,
            total: row.total,
            byPriority: row.byPriority || [],
          });
        }
      }
      return flattened.sort((a, b) => b.total - a.total).slice(0, limit);
    }
    const rows = breakdown[state] || [];
    return rows.slice(0, limit).map((row) => ({
      label: row.county,
      total: row.total,
      byPriority: row.byPriority || [],
    }));
  }

  const plain = countiesForStateOrAll(
    fallback?.countiesByState,
    state,
    limit,
    fallback?.globalCountyRows
  );
  return plain.map((row) => ({
    label: row.value,
    total: row.count,
    byPriority: [],
  }));
}

/** Fixed palette for priority_score stacked segments (higher = warmer). */
export function priorityScoreColor(priority: string): string {
  const n = Number(priority);
  if (n >= 5) return '#b91c1c';
  if (n >= 4) return '#ea580c';
  if (n >= 3) return '#ca8a04';
  if (n >= 2) return '#2563eb';
  return '#64748b';
}

/** Display label for the state filter control / chart titles. */
export function stateFilterLabel(state: string): string {
  if (state === ALL_STATES_FILTER) return 'All states';
  return state || '';
}

export function dailyTrendFieldLabel(
  kind: string | undefined,
  timeField: string | undefined
): string {
  if (timeField) return timeField;
  if (kind === 'tingOutages') return 'displaydate';
  if (kind === 'floodingBins') return 'capture_date';
  return 'date';
}

/** Aggregate FEMA totals from a FEMA×depth cross-tab. */
export function femaTotalsFromCross(
  cross: FemaDepthCrossRow[] | undefined | null
): GroupCountRow[] {
  if (!cross?.length) return [];
  const map = new Map<string, number>();
  for (const row of cross) {
    if (!row?.fema || typeof row.count !== 'number') continue;
    map.set(row.fema, (map.get(row.fema) ?? 0) + row.count);
  }
  return [...map.entries()]
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => b.count - a.count);
}

/** Depth breakdown for one FEMA class from the cross-tab. */
export function depthBreakdownForFema(
  cross: FemaDepthCrossRow[] | undefined | null,
  fema: string
): GroupCountRow[] {
  if (!cross?.length || !fema) return [];
  return cross
    .filter((r) => r.fema === fema)
    .map((r) => ({ value: r.depth, count: r.count }))
    .sort((a, b) => b.count - a.count);
}

/** Merge structure-exposure rows across states into one row per UTC day. */
export function aggregateStructureExposureByDay(
  rows: StructureExposureByDayRow[] | undefined | null
): StructureExposureByDayRow[] {
  if (!rows?.length) return [];
  const byDay = new Map<string, StructureExposureByDayRow>();
  for (const row of rows) {
    if (!row?.day) continue;
    const existing = byDay.get(row.day);
    if (!existing) {
      byDay.set(row.day, {
        day: row.day,
        state: ALL_STATES_FILTER,
        total: typeof row.total === 'number' ? row.total : 0,
        categories: { ...(row.categories || {}) },
      });
      continue;
    }
    if (typeof row.total === 'number' && Number.isFinite(row.total)) {
      existing.total += row.total;
    }
    for (const [k, v] of Object.entries(row.categories || {})) {
      if (typeof v !== 'number' || !Number.isFinite(v)) continue;
      existing.categories[k] = (existing.categories[k] ?? 0) + v;
    }
  }
  return [...byDay.values()].sort((a, b) => a.day.localeCompare(b.day));
}

/**
 * Filter structure-exposure rows to one state, or aggregate all states when
 * `state` is ALL_STATES_FILTER.
 */
export function structureExposureForState(
  rows: StructureExposureByDayRow[] | undefined | null,
  state: string
): StructureExposureByDayRow[] {
  if (!rows?.length || !state) return [];
  if (state === ALL_STATES_FILTER) {
    return aggregateStructureExposureByDay(rows);
  }
  return rows
    .filter((r) => r.state === state)
    .sort((a, b) => a.day.localeCompare(b.day));
}

/** Sorted unique days from structure-exposure rows (optionally filtered). */
export function structureExposureDays(
  rows: StructureExposureByDayRow[] | undefined | null
): string[] {
  const set = new Set<string>();
  for (const row of rows || []) {
    if (row?.day) set.add(row.day);
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}

/**
 * Align category series to a day axis for stacked bars. Returns map of
 * occupancy field → values aligned to `days`.
 */
export function structureExposureSeriesAligned(
  rows: StructureExposureByDayRow[] | undefined | null,
  days: string[],
  fields: readonly string[] = FLOOD_OCCUPANCY_FIELDS
): Record<string, number[]> {
  const byDay = new Map(rows?.map((r) => [r.day, r]) ?? []);
  const out: Record<string, number[]> = {};
  for (const field of fields) {
    out[field] = days.map((day) => {
      const row = byDay.get(day);
      const v = row?.categories?.[field];
      return typeof v === 'number' && Number.isFinite(v) ? v : 0;
    });
  }
  return out;
}

export function structureExposureTotalsAligned(
  rows: StructureExposureByDayRow[] | undefined | null,
  days: string[]
): number[] {
  const byDay = new Map(rows?.map((r) => [r.day, r]) ?? []);
  return days.map((day) => {
    const row = byDay.get(day);
    return typeof row?.total === 'number' && Number.isFinite(row.total)
      ? row.total
      : 0;
  });
}
